/**
 * BridgeAuth — Handles cross-product SSO from Foundations.
 * Receives a one-time opaque bridge code via URL, exchanges it for a PRISM JWT
 * through the backend (server-to-server verification with Foundations).
 *
 * URL: /auth/bridge?code=<opaqueCode>&redirect=<path>
 *
 * Security: The code is opaque (not a JWT), single-use, and expires in 30s.
 * The actual auth payload never appears in the URL or browser history.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { clearImpersonationStash } from '../services/impersonationSession';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export default function BridgeAuth() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const redirect = searchParams.get('redirect') || '/projects-v2';

    if (!code) {
      setError('No bridge code provided');
      return;
    }

    async function authenticate() {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/bridge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          setError(data.error || 'Bridge authentication failed');
          return;
        }

        // Store PRISM JWT
        // A new sign-in owns this browser: a stash left by an unfinished view-as must never outlive it.
        clearImpersonationStash();
        localStorage.setItem('prism_token', data.data.token);

        // Store user info for the app
        if (data.data.user) {
          localStorage.setItem('prism_user', JSON.stringify(data.data.user));
        }

        // Store bridge project ID for cross-product context
        if (data.data.project_id) {
          localStorage.setItem('prism_bridge_project_id', String(data.data.project_id));
        }

        // First-time Foundations users → onboarding wizard
        if (data.data.project_id && !data.data.wizard_completed) {
          navigate(`/projects-v2/${data.data.project_id}/foundations-onboarding`, { replace: true });
          return;
        }

        // Return visitors → intended PRISM page
        const targetPath = data.data.redirectPath ||
          (data.data.project_id ? `/projects-v2/${data.data.project_id}?from=foundations` : redirect);
        navigate(targetPath, { replace: true });
      } catch (err: any) {
        setError(err.message || 'Failed to connect to PRISM');
      }
    }

    authenticate();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', gap: 2, p: 3,
      }}>
        <Alert role="alert" severity="error" sx={{ maxWidth: 500 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          This link may have expired. Please return to Foundations and try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: '100vh', gap: 2,
    }}>
      <CircularProgress />
      <Typography variant="body1" color="text.secondary">
        Connecting to PRISM...
      </Typography>
    </Box>
  );
}
